import React from "react";
import { useLocation } from "react-router-dom";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { useAuth } from "@/hooks/auth";
import { useHeaderFadeVisible } from "@/hooks/useHeaderFadeVisible";
import { PAUSED_TINTS } from "@/layouts/components/AccountMenu";
import { colors } from "@/styles/design-system";
import { ERoutes } from "@/utils/constants";

/**
 * Site-wide banner shown on every page when the user is logged in.
 * Greeting + plan badge on the left, "Manage Claims" pill (with the
 * action-required count) on the right. Hidden when logged out.
 *
 * Replaces the old AccountLayout-only banner (which only appeared on
 * /account/* pages) and the per-page Claims main-nav item.
 *
 * The visible banner lives INSIDE the fixed `SecondHeaderContainer` so
 * it shares the header's fade/translate behavior on scroll. Because the
 * fixed container is taken out of the document flow, page content
 * doesn't know it just got 56px taller — the `<LoggedInBannerSpacer />`
 * (mounted in `SecondRootLayout`) injects that 56px of flow below the
 * header so the per-page top padding still leaves the same visual gap.
 */
export const LOGGED_IN_BANNER_HEIGHT = 56;

/** Spacer is no longer needed: the LoggedInBanner now renders inline
 *  in flow on /account/* (not portaled / not fixed), so it takes its
 *  own space naturally. Kept as a no-op for backward compat. */
export const LoggedInBannerSpacer: React.FC = () => null;

const PLAN_LABELS: Record<AccountType, string> = {
  [AccountType.FREE]: "Free Plan",
  [AccountType.SUBSCRIPTION]: "Subscription",
  [AccountType.MUSIC_CREATOR]: "Music Creator",
};

// Mirror the AccountLayout banner's tier pill colors so the look stays
// consistent — every tier is read straight from the design-system tier
// table (Music Creator gets the same blue treatment the old banner used).
const PLAN_TINTS: Record<AccountType, { bg: string; text: string }> = {
  [AccountType.FREE]: { bg: colors.tier.free.bg, text: colors.tier.free.text },
  [AccountType.SUBSCRIPTION]: { bg: colors.tier.subscription.bg, text: colors.tier.subscription.text },
  [AccountType.MUSIC_CREATOR]: { bg: "rgba(156, 211, 253, 0.1)", text: colors.link.blue },
};

const LoggedInBanner: React.FC = () => {
  const { isLoggedIn, isLoading, myData } = useAuth();
  const { accountType, pauseState } = useAccountType();
  const { pathname } = useLocation();
  const headerVisible = useHeaderFadeVisible();

  if (!isLoggedIn || isLoading) return null;

  // Only show the subheader on /account/* pages — but NOT on
  // /account/claims (that page already has the "Claims" pill in the
  // main header and a tier-aware description, so the banner would
  // duplicate state).
  const showOnThisPage =
    pathname.startsWith("/account")
    && !pathname.startsWith(ERoutes.AccountVideos);
  if (!showOnThisPage) return null;

  const firstName = (myData as any)?.firstName as string | undefined;
  // Paused accounts show the state, not the plan — see PAUSED_TINTS in
  // AccountMenu for why, and for the shared colours.
  const isPaused = pauseState !== "none";
  const planLabel = isPaused ? "Paused" : PLAN_LABELS[accountType];
  const planTint = isPaused ? PAUSED_TINTS[pauseState] : PLAN_TINTS[accountType];
  // Subscription page already shows the tier in its own header — hide here to avoid duplication.
  const showPlanPill = !pathname.startsWith(ERoutes.AccountSubscription);

  // On /account/* pages with a sidebar the website header is static
  // (not fixed), so the banner needs to be in flow too — that way both
  // scroll away together and the sticky sidebar can take over from
  // top: 0 without colliding. We render INLINE at the SecondRootLayout
  // mount point (no createPortal, no position: fixed, no fade).
  return (
    <div
      style={{
        width: "100%",
        background: "rgba(220, 180, 150, 0.22)",
        backdropFilter: "blur(64px)",
        WebkitBackdropFilter: "blur(64px)",
        borderBottom: "1px solid rgba(235, 194, 142, 0.1)",
        boxSizing: "border-box",
        opacity: headerVisible ? 1 : 0,
        transition: "opacity 0.3s ease",
        marginTop: -1,
      }}
    >
      <div
        style={{
          // Match the SecondHeader's container width + padding so the
          // banner's left/right edges align with the nav row above it.
          // box-sizing: border-box mirrors the MUI Grid's box model — the
          // 40px padding lives INSIDE the 1280 max-width, otherwise the
          // banner ends up ~80px wider than the nav.
          boxSizing: "border-box",
          width: "100%",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "18px 40px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          minHeight: 56,
        }}
      >
        {/* Left: greeting + plan badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span
            style={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: "1rem",
              fontWeight: 300,
              lineHeight: 1,
              color: "#ffffff",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {firstName ? `Hi, ${firstName}!` : "Hi!"}
          </span>
          {showPlanPill && (
            <span
              style={{
                fontFamily: '"Raleway", sans-serif',
                fontSize: "0.75rem",
                fontWeight: 500,
                lineHeight: 1,
                color: planTint.text,
                background: planTint.bg,
                borderRadius: 20,
                padding: "5px 12px",
                whiteSpace: "nowrap",
                letterSpacing: "0.3px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {planLabel}
            </span>
          )}
        </div>

        {/* Right: empty — Manage Claims lives in the main header now. */}
      </div>
    </div>
  );
};

export default LoggedInBanner;
